import { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { equals } from 'ramda';
import usePageType from 'hooks/usePageType';
import { getCountry } from 'assets/data/countries';

const useIntercom = () => {
  const location = useLocation();
  const userRef = useRef();
  const { activePaidContracts } = useSelector((state) => state.nomadInsurance.contract);
  const { user } = useSelector((state) => state.app);

  const { isNomadInsurance } = usePageType();
  const { planholder: nomadInsurancePlanholder } = useSelector(
    (state) => state.nomadInsurance
  );

  const currentContract =
    activePaidContracts && activePaidContracts.length > 0 && activePaidContracts[0];

  const getCurrentProduct = () => {
    if (isNomadInsurance) {
      return 'Nomad Insurance';
    }

    return 'Global';
  };

  const getPlanholder = () => {
    return nomadInsurancePlanholder;
  };

  const getActivePlanData = () => {
    return {
      activePolicyID: currentContract?.policyID || null,
      activePolicyStartDate: currentContract?.startDate,
      activePolicyEndDate: currentContract?.endDate,
      activePolicyNextPaymentDate: currentContract?.extensionDate,
      activePolicyPaymentStatus: currentContract?.paymentStatus,
      activePolicyType: currentContract?.type,
      activePolicyGroupMembers: currentContract?.groupMemberContracts
        ? currentContract?.groupMemberContracts.length
        : 0,
      hasUSAddon:
        currentContract?.addonContracts?.length > 0 &&
        currentContract.addonContracts.find(
          (addon) => addon.addonName === 'USA_COVERAGE'
        ),
      hasItemInsuranceAddon:
        currentContract?.addonContracts?.length > 0 &&
        currentContract.addonContracts.find(
          (addon) => addon.addonName === 'ITEM_INSURANCE'
        ),
      hasSportsAddon:
        currentContract?.addonContracts?.length > 0 &&
        currentContract.addonContracts.find(
          (addon) => addon.addonName === 'SPORTS_COVERAGE'
        )
    };
  };

  const getIntercomUser = () => {
    const { planholder } = getPlanholder();

    const intercomUser = {
      user_id: user?.userID || null,
      currentProduct: getCurrentProduct()
    };

    if (user?.isAuthenticated && user?.companyId) {
      Object.assign(intercomUser, {
        company: {
          id: user.companyId || null
        }
      });
    }

    if (user?.isAuthenticated && planholder) {
      Object.assign(intercomUser, {
        ...planholder,
        email: planholder?.email,
        name: planholder.firstName
          ? `${planholder.firstName} ${planholder.lastName}`
          : planholder?.email,
        firstName: planholder?.firstName,
        lastName: planholder?.lastName,
        homeCountry: planholder?.homeCountry ? getCountry(planholder.homeCountry) : null,
        citizenshipCountry:
          planholder?.citizenshipCountry || planholder?.citizenship
            ? getCountry(planholder.citizenshipCountry || planholder?.citizenship)
            : null,
        country: planholder?.address?.country
          ? getCountry(planholder?.address?.country)
          : null,
        city: planholder?.address?.city,
        phone:
          planholder.phoneCountryCode &&
          planholder.phoneNumber &&
          `${planholder.phoneCountryCode}${planholder.phoneNumber}`
      });
    }

    if (isNomadInsurance && currentContract) {
      Object.assign(intercomUser, getActivePlanData());
    }

    return intercomUser;
  };

  const intercomUser = getIntercomUser();
  const memoizedIntercomUser = useMemo(() => {
    if (!equals(intercomUser, userRef.current)) {
      userRef.current = intercomUser;
      return intercomUser;
    }
    return userRef.current;
  }, [intercomUser]);

  useEffect(() => {
    window.Intercom('boot', {
      app_id: process.env.REACT_APP_INTERCOM_APP_ID,
      api_base: `https://${process.env.REACT_APP_INTERCOM_APP_ID}.intercom-messenger.com`,
      ...memoizedIntercomUser
    });
    // shut down intercom before unload so conversations of
    // previous non-logged in user are not visible to next user
    window.addEventListener('beforeunload', () => {
      window.Intercom('shutdown');
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (memoizedIntercomUser.user_id) {
      window.Intercom('update', {
        ...memoizedIntercomUser,
        last_request_at: parseInt(new Date().getTime() / 1000, 10)
      });
    }
  }, [memoizedIntercomUser, location.pathname]);
};

export default useIntercom;
